import { Flex } from '@chakra-ui/layout';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CellContext,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router';

import { Funding } from '@finley-technologies/finley-types';

import { duplicateCommitment, deleteCommitment } from './dataMappings';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { remToPx } from 'packages/common-frontend/theme/utils';
import { usePreviousAndCurrentCommitmentFormData } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/hooks';
import { useConfirmDeleteContext } from '~/core/routes/settings/implementations/common/ConfirmDeleteModalContext';
import { useConfirmDuplicateContext } from '~/core/routes/settings/implementations/common/ConfirmDuplicateModalContext';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  DiffTag,
  ShowWhileDiffing,
  ShowWhileEditing,
} from '~/core/routes/settings/implementations/common/DiffInputs';
import { computeDiff } from '~/core/routes/settings/implementations/common/DiffInputs/hooks';

type TableData = Funding.LoanMechanicsWithEntities['commitments'][0];

const RenderMenu = ({ row, table }: CellContext<TableData, any>) => {
  const updateCreditAgreement = useCreditAgreementStore(
    (store) => store.updateCreditAgreement,
  );
  const menuControls = useDisclosure();
  const { showConfirmDeleteModal } = useConfirmDeleteContext();
  const { showConfirmDuplicateModal } = useConfirmDuplicateContext();

  const showConfirmDuplicateModalPayload = {
    existingEntityName: row.original.name,
    callback: (newName: string) =>
      updateCreditAgreement((d) =>
        duplicateCommitment(d, row.original.key, newName),
      ),
    existingNames: table.getRowModel().rows.map((r) => r.original.name),
  };

  return (
    <ShowWhileEditing>
      <Flex
        justify="right"
        // stopPropagation() here prevents the onClick handler on table rows from being triggered.
        // See the 'onRowClick' handler on the TableBETA component
        onClick={(e) => e.stopPropagation()}
        overflow="hidden"
      >
        <Menu {...menuControls} computePositionOnMount strategy="fixed">
          <MenuButton
            as={IconButtonV2}
            variant="secondary-muted"
            colorScheme="gray"
            isActive={menuControls.isOpen}
            aria-label="Facility options"
            icon={<Icon icon="ellipsis-vertical" />}
            tooltipEnabled={false}
          />
          <MenuList zIndex="dropdown">
            <MenuItem
              onClick={() =>
                showConfirmDuplicateModal(showConfirmDuplicateModalPayload)
              }
            >
              Duplicate Commitment
            </MenuItem>
            <MenuItem
              onClick={() =>
                showConfirmDeleteModal(row.original.name, () =>
                  updateCreditAgreement((d) =>
                    deleteCommitment(d, row.original.key),
                  ),
                )
              }
            >
              Delete Commitment
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </ShowWhileEditing>
  );
};

const CommitmentDiffTag = ({ commitmentKey }: { commitmentKey: string }) => {
  const {
    commitmentFormData,
    previousCommitmentFormData,
  } = usePreviousAndCurrentCommitmentFormData(commitmentKey);

  const diffResult = computeDiff(
    commitmentFormData,
    previousCommitmentFormData,
    _.isEqual,
    String,
  );

  return <DiffTag diff={diffResult} disableTooltip />;
};

const RenderCommitmentName = ({
  row,
  getValue,
}: CellContext<TableData, 'name'>) => {
  return (
    <Flex gap="1rem" alignItems="baseline">
      <ShowWhileDiffing>
        <CommitmentDiffTag commitmentKey={row.original.key} />
      </ShowWhileDiffing>
      {getValue()}
    </Flex>
  );
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const columnHelper = createColumnHelper<TableData>();
const columnDefs = [
  columnHelper.accessor('name', {
    header: 'Commitment Name',
    enableSorting: false,
    cell: RenderCommitmentName,
  }),
  columnHelper.accessor('limit.amount', {
    header: 'Commitment Amount',
    enableSorting: false,
    cell: ({ getValue }) => currencyFormatter.format(getValue()),
  }),
  columnHelper.accessor('limit.sublimit', {
    header: 'Sublimit',
    enableSorting: false,
    cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
  }),
  columnHelper.display({
    id: 'editCommitment',
    header: '',
    enableSorting: false,
    minSize: remToPx(1),
    size: remToPx(1),
    cell: RenderMenu,
  }),
];

export const CommitmentTable = () => {
  const allCommitments = useCreditAgreementStore((store) => {
    const commitments =
      store.creditAgreement?.loanMechanicsV1?.commitments ?? [];
    if (!store.isEditing) {
      const previousCommitments =
        store.previousCreditAgreement?.loanMechanicsV1?.commitments ?? [];
      return _.unionBy(
        commitments,
        previousCommitments,
        (commitment) => commitment.key,
      );
    }
    return commitments;
  }, _.isEqual);

  const table = useReactTable({
    data: allCommitments,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
  });

  const navigate = useNavigate();

  return (
    <TableBETA
      table={table}
      onRowClick={(row) => navigate(`${row.original.key}`)}
    />
  );
};
